import { Outlet, Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search, User } from 'lucide-react';
import { useState } from 'react';
import { useCarrinhoStore } from '@/stores/carrinhoStore';
import { useAuthStore } from '@/stores/authStore';
import { CookieConsent } from '@/components/loja/CookieConsent';
import { useConfiguracoesBanner } from '@/hooks/useConfiguracoes';

const navLinks = [
  { label: 'Início', href: '/' },
  { label: 'Catálogo', href: '/catalogo' },
  { label: 'Blog', href: '/blog' },
  { label: 'Sobre', href: '/sobre' },
  { label: 'Contato', href: '/contato' },
];

export function LojaLayout() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const totalItems = useCarrinhoStore((state) => state.totalItems());
  const { isAuthenticated, tipo } = useAuthStore();
  const { data: config } = useConfiguracoesBanner();

  const handleUserClick = () => {
    if (isAuthenticated) {
      if (tipo === 'admin') {
        navigate('/admin');
      } else {
        navigate('/minha-conta');
      }
    } else {
      navigate('/login');
    }
  };

  const bannerContent = config?.bannerAtivo ? (
    config.bannerLink ? (
      <a 
        href={config.bannerLink} 
        className="block hover:underline"
        style={{ color: config.bannerCorTexto }}
      >
        {config.bannerTexto}
      </a>
    ) : (
      <span>{config.bannerTexto}</span>
    )
  ) : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      {config?.bannerAtivo && (
        <div 
          className="text-xs py-1.5 transition-colors"
          style={{ 
            backgroundColor: config.bannerCorFundo,
            color: config.bannerCorTexto 
          }}
        >
          <div className="max-w-7xl mx-auto px-4 text-center">
            {bannerContent}
          </div>
        </div>
      )}

      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex h-14 items-center justify-between gap-4">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -ml-2"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? (
                <X className="h-6 w-6 text-gray-700" />
              ) : (
                <Menu className="h-6 w-6 text-gray-700" />
              )}
            </button>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0 group">
              <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                <img src="/images/branding/icon.png" alt="Fort3D Icon" className="h-9 w-9 object-contain group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span className="text-xl font-black text-gray-900 hidden sm:block tracking-tight italic uppercase">Fort<span className="text-fort3d-laranja">3D</span></span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-gray-600 hover:text-primary font-medium transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button 
                className="p-2 text-gray-600 hover:text-primary transition-colors"
                onClick={() => setSearchOpen(!searchOpen)}
              >
                <Search className="h-5 w-5" />
              </button>
              
              <button 
                onClick={handleUserClick}
                className="p-2 text-gray-600 hover:text-primary transition-colors"
              >
                <User className="h-5 w-5" />
              </button>
              
              <Link to="/carrinho" className="relative p-2 text-gray-600 hover:text-primary transition-colors">
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center rounded-full bg-primary text-white text-xs font-bold">
                    {totalItems}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {searchOpen && (
          <div className="border-t border-gray-100 py-4 px-4 animate-fade-in-up">
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar produtos..."
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                autoFocus
              />
            </div>
          </div>
        )}

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white">
            <nav className="flex flex-col p-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="py-3 text-gray-700 font-medium border-b border-gray-50 last:border-0"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-gray-50 mt-2 pt-2">
                <button
                  onClick={() => {
                    handleUserClick();
                    setMenuOpen(false);
                  }}
                  className="w-full flex items-center gap-3 py-3 text-gray-700 font-medium"
                >
                  <User className="h-5 w-5 text-gray-400" />
                  {isAuthenticated ? (tipo === 'admin' ? 'Painel Administrativo' : 'Minha Conta') : 'Entrar / Cadastrar'}
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main */}
      <main className="min-h-[calc(100vh-400px)]">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-1">
              <div className="flex flex-col gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 flex items-center justify-center overflow-hidden bg-white/10 rounded-xl">
                    <img src="/images/branding/icon.png" alt="Fort3D Icon" className="h-7 w-7 object-contain" />
                  </div>
                  <span className="text-xl font-bold">Fort3D</span>
                </div>
                <img src="/images/branding/logo.jpg" alt="Fort3D Logo Full" className="w-48 rounded-lg opacity-90 hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Sua loja de impressão 3D profissional. Produtos personalizados com qualidade e precisão.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Categorias</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/catalogo" className="hover:text-white transition-colors">Acessórios</Link></li>
                <li><Link to="/catalogo" className="hover:text-white transition-colors">Decoração</Link></li>
                <li><Link to="/catalogo" className="hover:text-white transition-colors">Utilitários</Link></li>
                <li><Link to="/catalogo" className="hover:text-white transition-colors">Prototipagem</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Atendimento</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/contato" className="hover:text-white transition-colors">Contato</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog</Link></li>
                <li><Link to="/sobre" className="hover:text-white transition-colors">Sobre Nós</Link></li>
                <li><a href="https://wa.me/5547997679994" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">WhatsApp</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/politica-de-privacidade" className="hover:text-white transition-colors">Política de Privacidade</Link></li>
                <li><Link to="/politica-de-cookies" className="hover:text-white transition-colors">Política de Cookies</Link></li>
                <li><Link to="/termos-de-uso" className="hover:text-white transition-colors">Termos de Uso</Link></li>
              </ul>
              <div className="flex gap-3 mt-4">
                <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors">
                  <span className="sr-only">Instagram</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-gray-800 text-center text-sm text-gray-400">
            <p>© 2024 Fort3D Indústria e Comércio LTDA. Todos os direitos reservados.</p>
            <p className="mt-2">CNPJ: 00.000.000/0000-00 | São Paulo, SP - Brasil</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent */}
      <CookieConsent />
    </div>
  );
}
